/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { memo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import ReactLoading from 'react-loading';

import NextButton from "components/ui/button/NextButton";
import BackButton from "components/ui/button/BackButton";
import InputTextArea from "components/ui/input/InputTextArea";
import AccordionOnCheckbox from "components/ui/accordion/AccordionOnCheckbox";
import LabelWithOptionIcon from "components/ui/label/LabelWithOptionIcon";

import ImageUpload from "components/parts/image/imageUpload/ImageUpload";

// 入力フォームのstore保存
import { useStateMachine } from "little-state-machine";
import { getStore, kikiSelectAction } from "stores/actions.js";

// CSS読み込み
import { mtgGroup, mtgBody } from "assets/css/stylesCss";
import { loadingCss } from "assets/css/shchMtmr";

// GQL
import useGetQuery from "hooks/useGetQuery";
import { GET_KIKI_LIST } from "graphql/query";

/**
 * 基本情報入力画面
 */
const KikiSelect = memo(() => {

  // 画面遷移用
  const navigate = useNavigate();

  // store取得・更新
  const { state } = useStateMachine(getStore);
  const { actions } = useStateMachine({
    kikiSelectAction,
  });

  // 前画面までの必要項目が不足している場合エラー

  const methods = useForm({
    defaultValues: {
      isActive: false,
      selectKiki: state.storeKikiSelect.selectKiki,
      selectKikiNm: state.storeKikiSelect.selectKikiNm,
      request: state.storeKikiSelect.request,
      tmpImage1: state.storeKikiSelect.tmpImage1,
      tmpImageMine1: state.storeKikiSelect.tmpImageMine1,
      tmpImage2: state.storeKikiSelect.tmpImage2,
      tmpImageMine2: state.storeKikiSelect.tmpImageMine2,
      tmpImage3: state.storeKikiSelect.tmpImage3,
      tmpImageMine3: state.storeKikiSelect.tmpImageMine3,
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  // 機器選択一覧を取得するクエリ
  const [getKikiMtmrSelectList, error, loading] = useGetQuery(GET_KIKI_LIST, "");
  // エラー発生時
  if (error) {
    navigate("/error", {
      state: { errorCode: 500 },
    });
  }

  // 次画面に遷移
  const onSubmit = (data) => {
    // 送信前に選択機器の名称を取得
    data.selectKikiNm = getArrSelectNm("selectKiki");
    // 大分類value値をselectKikiにpush
    data = dibnriPush(data);
    // Actionに設定
    actions.kikiSelectAction(data);
    navigate("/contract", {
      state: { isSubmit: true },
    });
  };

  return (
    <>
      {!loading && (
        <>
          <h1>機器の選択</h1>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>

              {/** 機器の選択 */}
              <div css={mtgGroup}>
                <p css={mtgBody}>
                  見積もりを依頼したい機器を選んでください。
                </p>

                {/** 機器相談アコーディオンチェックボックス */}
                {getKikiMtmrSelectList.getKikiMtmrSelectList.kikiMtmrSelectList.map((item, index) => (
                  <AccordionOnCheckbox
                    key={index}
                    title={item.kbnName}
                    isChecked={isChecked(item, state.storeKikiSelect.selectKiki)}
                  >

                    {/** 機器の選択チェックボックス一覧を生成 */}
                    {item.kikSelectiList.map((kikSelectiList, index) => (
                      <p
                        key={index}
                        name="selectKiki"
                        value={kikSelectiList.value}
                        hyojiJn={kikSelectiList.hyojiJn}>{kikSelectiList.label}
                      </p>
                    ))}
                  </AccordionOnCheckbox>
                ))}
              </div>

              {/** ご要望 */}
              <div css={mtgGroup}>
                <LabelWithOptionIcon required={false}>
                  ご要望
                </LabelWithOptionIcon>
                <InputTextArea
                  name="request"
                  maxLength="500"
                  placeholder="新しい機器へのご要望や、現状の機器へのご不満などがございましたらお聞かせください。"
                ></InputTextArea>
              </div>

              {/** 写真データ */}
              <div css={mtgGroup}>
                <ImageUpload
                  imageName1="tmpImage1" mineName1="tmpImageMine1"
                  imageName2="tmpImage2" mineName2="tmpImageMine2"
                  imageName3="tmpImage3" mineName3="tmpImageMine3"
                />
              </div>

              {/* お申込み条件確認 */}
              <NextButton
                isActive={isValid}
                value={isValid ? "次へ" : "次へ"}
                isInitialState={false}
              ></NextButton>

            </form>

            {/** 戻る */}
            <BackButton url={"/AppointmentCalendar"} />
          </FormProvider>
        </>
      )}

      {/** ローディング */}
      {loading && (
        <div css={[loadingCss]}>
          <ReactLoading type="spin" color="#b5b5b5" height="50px" width="50px" />
        </div>
      )}
    </>
  );
});

/**
 * 選択されている名称を取得し返却
 * @returns resultArr 選択されている名称配列
 */
const getArrSelectNm = (name) => {

  var resultArr = [];

  const checkArr = document.getElementsByName(name);
  checkArr.forEach(element => {
    if (element.checked === true) {
      resultArr.push(element.parentElement.textContent);
    }
  });

  return resultArr;
}

/**
 * チェックされているチェックボックスが存在するか
 * @param {*} item アコーディオン内のチェックボックス項目
 * @param {*} selectKiki 選択済みのチェックボックス値
 * @returns true : 機器選択あり
 *          false: 機器選択なし
 */
const isChecked = (item, selectKiki) => {

  var result = false;

  // 機器が選択されているか判定
  item.kikSelectiList.forEach(kikSelectiList => {
    selectKiki.forEach(selectKikiInfo => {
      if (selectKikiInfo === kikSelectiList.value) {
        result = true;
      }
    });
  });

  // 機器の選択がされていない場合、最上部のみオープン
  if (selectKiki.length === 0 && item.kbnName === 'キッチン関連') {
    result = true;
  }

  return result;
}

/** 選択機器一覧に大分類を追加 */
const dibnriPush = (data) => {
  const selectKiki = data.selectKiki;
  selectKiki.forEach(value => {
    if (selectKiki.indexOf(value) !== -1) {
      selectKiki.push(getDibnriVal(value))
    }
  });
  data.selectKiki = selectKiki;
  return data;
}

/** 子分類から大分類を取得 */
/** べた書きなので将来的にはマスタ化で対応？ */
export const getDibnriVal = (value) => {
  if (value === "builtInKnro" || value === "gasTable" || value === "rangeFood" || value === "gasOven" || value === "suisenJoski" || value === "washKnsoki") {
    return "ktnKnrn";
  }
  if (value === "heater" || value === "kantaKun" || value === "bathSuisen" || value === "shower") {
    return "bathKnrn";
  }
  if (value === "toileSuisen" || value === "toilet" || value === "onsuiSeat") {
    return "toiletKnrn";
  }
  if (value === "nookHywz" || value === "gasFaHeater" || value === "airCon") {
    return "livingKnrn";
  }
  if (value === "enefarm" || value === "taiyoko" || value === "battery" || value === "gasKyutoki" || value === "tes") {
    return "kytkHtdnEne";
  }
  if (value === "keihoki" || value === "shokaki") {
    return "safety";
  }
  if (value === "door" || value === "amido" || value === "cloth" || value === "illmiLed" || value === "otherInput") {
    return "other";
  }
}

export default KikiSelect;
